import UserSay from '@/components/UserSay'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { services } from '@/lib/api'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '@/store/userStore'

type UserRole =
  | 'Admin'
  | 'SuperAdmin'
  | 'Jerid_School_Admin'
  | 'Teacher'
  | 'Parent'
  | 'Student'
  | 'B2C_Client'
  | string

export const Route = createFileRoute('/login')({
  component: RouteComponent,
})

function RouteComponent() {
  return <Login />
}

function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  const { mutate, isPending } = useMutation({
    mutationFn: services.auth.login,
    onSuccess: (data) => {
  // console.logLogin successful:', data)
      setLoginError(null)

      // Create user object with all the data from the response
      // Backend sets HTTP-only cookie automatically, no token needed
      const userData = data.user as any
      const user = {
        id: data.user.id,
        firstname: data.user.firstname,
        lastname: data.user.lastname,
        email: data.user.email,
        role: data.user.role,
        avatar: userData.avatar,
        fullname: userData.fullname,
        userOnboarding: data.user.userOnboarding,
        isPaid: data.user.role === 'Student' ? userData.isPaid : undefined,
        establishmentId: userData.establishmentId,
      }

      // Use the user store to handle login (HTTP-only cookie is set by backend)
      useUserStore.getState().login(user)

      let redirectPath = '/'
      switch (data.user.role as UserRole) {
        case 'Admin':
          redirectPath = '/admin'
          break
        case 'SuperAdmin':
          redirectPath = '/super_admin'
          break
        case 'Jerid_School_Admin':
          redirectPath = '/jerid_school_admin'
          break
        case 'Teacher':
          redirectPath = '/teacher'
          break
        case 'Parent':
          redirectPath = '/parent'
          break
        case 'Student':
          redirectPath = '/student'
          break
        case 'B2C_Client':
          redirectPath = '/b2c_student'
          break
        default:
          redirectPath = '/'
      }

      navigate({ to: redirectPath })
    },
    onError: (error: any) => {
      // console.error('Login error:', error)

      if (error.response?.status === 401) {
        setLoginError(t('routes.login.errors.invalid-credentials'))
      } else if (error.response?.data?.message) {
        setLoginError(error.response.data.message)
      } else if (error.message) {
        setLoginError(error.message)
      } else {
        setLoginError(t('routes.login.errors.default'))
      }
    },
  })

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        setLoginError(null)
        mutate(value)
      } catch (error) {
        // console.error('Form submission error:', error)
      }
    },
  })

  return (
    <div className="flex h-screen mt-[1px]">
      <div className="flex-1 flex items-center justify-center ">
        <div className="w-full max-w-md space-y-8 px-4 mt-[-250px]">
          <UserSay
            blueWord={t('routes.login.welcome.blue-word')}
            title={t('routes.login.welcome.title')}
            description={t('routes.login.welcome.description')}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div>
                <form.Field
                  name="email"
                  children={(field) => (
                    <input
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder={t('routes.login.form.inputs.email')}
                    />
                  )}
                />
              </div>
              <div>
                <form.Field
                  name="password"
                  children={(field) => (
                    <div>
                      <div className="relative">
                        <input
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type={isPasswordVisible ? 'text' : 'password'}
                          className={`w-full px-4 py-2 border ${loginError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none`}
                          placeholder={t('routes.login.form.inputs.password')}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {isPasswordVisible ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </button>
                      </div>
                      {loginError && (
                        <p className="mt-1 text-sm text-red-600">
                          {loginError}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-secondary focus:ring-secondary border-gray-300 rounded"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    {t('routes.login.form.remember-me')}
                  </label>
                </div>
                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-black hover:text-[#F59E0B]"
                  >
                    {t('routes.login.form.forgot-password')}
                  </Link>
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-3xl shadow-sm text-sm font-medium text-white bg-[#FCD34D] hover:bg-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FCD34D] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isPending
                    ? t('routes.login.form.buttons.signing-in')
                    : t('routes.login.form.buttons.sign-in')}
                </button>
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">
                    {t('routes.login.form.register.text')}{' '}
                    <Link
                      to="/register"
                      className="text-[#F59E0B] hover:text-[#FCD34D] font-medium"
                    >
                      {t('routes.login.form.register.link')}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </form>
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('routes.login.divider')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="/images/auth/jeirdooo_superman.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  )
}
